// frontend/components/ui/ToastContainer.tsx

"use client";

import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

interface ToastItem {
  id: string;
  message: string;
  action?: { label: string; onClick: () => void };
}

export default function ToastContainer() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [mounted, setMounted] = useState(false);

  useEffect(() => { setMounted(true); }, []);

  const remove = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const { message, action } = (e as CustomEvent).detail;
      const id = Math.random().toString(36).slice(2) + Date.now().toString(36);
      setToasts(prev => [...prev, { id, message, action }]);
      setTimeout(() => remove(id), 6000);
    };
    window.addEventListener("show-toast", handler);
    return () => window.removeEventListener("show-toast", handler);
  }, [remove]);

  if (!mounted || toasts.length === 0) return null;

  return createPortal(
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-9999 flex flex-col items-center gap-2 pointer-events-none">
      {toasts.map(toast => (
        <div
          key={toast.id}
          className="pointer-events-auto flex items-center gap-3 bg-zinc-800 text-white pl-4 pr-2 py-3 rounded-xl shadow-2xl text-sm whitespace-nowrap"
        >
          <span className="text-zinc-100">{toast.message}</span>
          {toast.action && (
            <button
              onClick={() => { toast.action!.onClick(); remove(toast.id); }}
              className="text-blue-400 hover:text-blue-300 font-semibold transition-colors"
            >
              {toast.action.label}
            </button>
          )}
          <button onClick={() => remove(toast.id)} className="text-zinc-500 hover:text-zinc-200 transition-colors p-1">
            <X size={13} />
          </button>
        </div>
      ))}
    </div>,
    document.body
  );
}
